import { useSmartNavigation } from "@/components/r3f/controls/smart-navigation/use-smart-navigation";
import { UnknownObject } from "@/object-cache-type-guard";
import {
  ExplorationControls,
  Map2DControls,
  SphereControls,
} from "@faro-lotv/app-component-toolbox";
import {
  ForwardMovement,
  PointerRotates,
  WalkOrbitControls,
} from "@faro-lotv/lotv";
import { useThree } from "@react-three/fiber";
import { JSX, useCallback, useEffect, useState } from "react";
import { Vector3, type Camera } from "three";
import { FlyWalkAnimation } from "./data-dispatch/flywalk-animation";
import { ImageType } from "./data-dispatch/flywalk-data-dispatch";
import { useToolsAcquaintance } from "./tools/tools-acquaintance-context";

type FlyWalkControlsProps = {
  /** The image type of the current main element */
  imageType: ImageType;
  /** The current animation in flywalk mode */
  animation: FlyWalkAnimation | null;
  /** Controls rotation pivot */
  target: Vector3 | undefined;
  /** Whether a forward translation means walk forward or fly forward*/
  forwardMovement: ForwardMovement | undefined;
  /** Models clickable by controls */
  clickableByControls: UnknownObject[];
  /** True if the SCENE-like smart navigation can be used in this scene */
  smartNavigationAvailable?: boolean;
  /** Callback issued when a new instance of exploration controls is mounted */
  setControls?(controls: WalkOrbitControls | null): void;
  /** Optional callback to set the rotation pivot */
  setTarget?(target: Vector3 | undefined): void;
  /** Callback to set the camera the user is interacting with */
  setActiveCamera?(camera: Camera): void;
  /** Callback issued when a camera movement via controls finishes */
  onCameraMovedViaControls?(pos: Vector3): void;
};

/** @returns The camera controls in fly walk mode */
export function FlyWalkControls({
  imageType,
  animation,
  target,
  forwardMovement,
  clickableByControls,
  smartNavigationAvailable = false,
  setControls,
  setTarget,
  setActiveCamera,
  onCameraMovedViaControls,
}: FlyWalkControlsProps): JSX.Element | null {
  const {
    onCameraStoppedTranslating,
    activeToolAndPicking,
    guidedNavigation,
    isGuidedNavigationActive,
  } = useToolsAcquaintance();

  const camera = useThree((s) => s.camera);

  const cameraStopped = useCallback(() => {
    onCameraStoppedTranslating.emit();
    onCameraMovedViaControls?.(camera.position.clone());
  }, [onCameraStoppedTranslating, onCameraMovedViaControls, camera]);

  const [controlsInstance, setControlsInstance] =
    useState<WalkOrbitControls | null>(null);
  const updateControls = useCallback(
    (controls: WalkOrbitControls | null) => {
      setControlsInstance(controls);
      setControls?.(controls);
    },
    [setControls],
  );

  useEffect(() => {
    if (!controlsInstance || !isGuidedNavigationActive) return;
    controlsInstance.translationConstraint = guidedNavigation.constrain;
    return () => {
      controlsInstance.translationConstraint = undefined;
    };
  }, [controlsInstance, isGuidedNavigationActive, guidedNavigation]);

  useSmartNavigation(
    smartNavigationAvailable &&
      imageType === ImageType.NoImage &&
      !activeToolAndPicking.isPickingToolActive,
    controlsInstance,
    clickableByControls,
    setTarget,
  );

  const enabled = animation === null;

  const onUserInteracted = useCallback(() => {
    setActiveCamera?.(camera);
  }, [setActiveCamera, camera]);

  if (imageType === ImageType.Pano) {
    return (
      <SphereControls
        isDefault
        isEnabled={enabled}
        inertia={0}
        onUserInteracted={onUserInteracted}
      />
    );
  } else if (
    imageType === ImageType.Orthomosaic ||
    imageType === ImageType.Photo2D
  ) {
    return <Map2DControls enabled={enabled} />;
  }

  // Suppress the click-to-focus camera re-aim for clicks inside the smart
  // navigation circle, there a click is the start of a look-around instead
  const enableRotationPivot =
    clickableByControls.length > 0 && !activeToolAndPicking.isPickingToolActive;

  return (
    <ExplorationControls
      ref={updateControls}
      enabled={enabled}
      target={target}
      obstacles={[]}
      clickableModels={clickableByControls}
      enableClickToFocus={enableRotationPivot}
      enableDoubleClickToZoom={enableRotationPivot}
      pointerRotates={PointerRotates.ViewDirection}
      forwardMovement={forwardMovement}
      disableInertia
      onCameraStoppedTranslating={cameraStopped}
      onUserInteracted={onUserInteracted}
    />
  );
}
